import type { HandoffStatus } from "./handoff";

const INITIAL_DELAY_MS = 1000;
const BACKOFF_FACTOR = 1.5;
const MAX_DELAY_MS = 3000;
const MAX_RETRIES = 6;

/** The wait before retry `retryIndex` (0-based), or null once the retries
 * are spent. */
export function handoffRetryDelayMs(retryIndex: number): number | null {
  if (retryIndex < 0 || retryIndex >= MAX_RETRIES) return null;
  return Math.min(
    Math.round(INITIAL_DELAY_MS * BACKOFF_FACTOR ** retryIndex),
    MAX_DELAY_MS,
  );
}

/** Asks until the answer is anything but `none`, waiting per the backoff
 * schedule between asks. Returns the last answer, or null when cancelled. */
export async function pollHandoffStatus(options: {
  check: () => Promise<HandoffStatus>;
  wait: (ms: number) => Promise<void>;
  isCancelled: () => boolean;
}): Promise<HandoffStatus | null> {
  const { check, wait, isCancelled } = options;
  for (let retry = 0; ; retry++) {
    if (isCancelled()) return null;
    const status = await check();
    if (isCancelled()) return null;
    if (status.kind !== "none") return status;
    const delay = handoffRetryDelayMs(retry);
    if (delay === null) return status;
    await wait(delay);
  }
}
